import React, {
  useReducer,
  useEffect,
  useCallback,
  useMemo,
} from 'react';
import _ from 'lodash';
import { useQuery, useMutation } from '@apollo/client';
import { compose } from 'redux';
import { connect } from 'react-redux';
import PropTypes from 'prop-types';

import { ErrorHandler, ShareLinkDialog as ShareDialog } from '@playbookux/playbook-web-core';
import Froala from '../../../components/froala';
import { processVideoUrls } from '../../../components/froala/utils';
import Tabs from '../../../constants/tabs';
import withProjectSidebar from '../../../components/with-project-sidebar';
import { useDebounce } from '../../../../core/hooks';
import useDispatchToActions from '../../../../core/utils/dispatch';
import { config } from '../../../../config';

import {
  Container,
  FeedbackMessage,
  Input,
  Loader,
  ContainerHeader,
  ContainerFroala,
  Button,
} from './styles';

import reducer, {
  initialState,
} from './reducer';

import {
  setFeedbackSaved,
  setModalOpen,
  setReport,
  setReportData,
  setSaving,
  setTitle,
  setShareModalOpen,
} from './actions';

import {
  UPDATE_REPORTS,
  GET_REPORT_BY_PROJECT_ID,
  GET_COMPANY_MEMBERS,
} from './graphql';

const DEBOUNCE_TIME = 700;

const ReportEdit = (props) => {
  const { project, match, userProfile } = props;
  const { params } = match;
  const { reportId } = params;

  const [state, dispatch] = useReducer(reducer, initialState);

  const {
    data = {},
    loading,
    error,
  } = useQuery(GET_REPORT_BY_PROJECT_ID, {
    variables: { projectId: project.id },
  });

  const { reports: reportQuery = {} } = data;

  const {
    data: companyMembersData = {},
    loading: companyMembersLoading,
    error: companyMembersError,
  } = useQuery(GET_COMPANY_MEMBERS, {
    variables: {
      companyId: project.company.id,
    },
    skip: !project.company.id,
  });

  const [updateReports] = useMutation(UPDATE_REPORTS);

  const dispatchToActions = useDispatchToActions({
    setFeedbackSaved,
    setModalOpen,
    setReport,
    setReportData,
    setSaving,
    setTitle,
    setShareModalOpen,
  }, dispatch);

  const {
    touched,
    feedback,
    report,
    modalOpen,
    shareModalOpen,
  } = state;

  const {
    title,
    reportData,
    shareCode,
    sharePassword,
    shareEnabled,
    testSessionNotes,
    highlightReels,
  } = report;

  const {
    setFeedbackSaved: onSetFeedbackSaved,
    setModalOpen: onSetModalOpen,
    setReport: onSetReport,
    setReportData: onSetReportData,
    setSaving: onSetSaving,
    setTitle: onSetTitle,
    setShareModalOpen: onSetShareModalOpen,
  } = dispatchToActions;

  const handleUpdate = async () => {
    await updateReports({
      variables: {
        id: reportId,
        projectId: project.id,
        attributes: {
          name: title,
          reportData,
          shareEnabled,
          sharePassword,
        },
      },
    });
  };

  const handleTitleChange = ({ target }) => {
    const { value: newTitle } = target;
    onSetTitle(newTitle);
  };

  const handleModalOpen = () => {
    onSetModalOpen(modalOpen);
  };

  const handleReportDataChange = (val) => {
    if (val === reportData) return;
    onSetReportData(val);
  };

  useEffect(() => {
    if (!loading && !_.isEmpty(reportQuery)) {
      onSetReport(reportQuery[0]);
    }
  }, [loading, reportQuery, onSetReport]);

  useDebounce(async () => {
    if (loading || !touched) return;
    onSetSaving();
    await handleUpdate();
    onSetFeedbackSaved();
  }, DEBOUNCE_TIME, [title, reportData]);

  const handleUpdateShare = (password, enabled) => {
    updateReports({
      variables: {
        id: reportId,
        projectId: project.id,
        attributes: {
          name: title,
          reportData,
          shareEnabled: enabled,
          sharePassword: password,
        },
      },
    });
  };

  const handleFetchMembers = useCallback((cb) => {
    const { companyMembers } = companyMembersData;
    if (Array.isArray(companyMembers)) {
      const mapped = companyMembers.map((cm) => {
        if (cm.user.profile && cm.user.profile.firstName && cm.user.profile.lastName) {
          return {
            key: `${cm.user.profile.firstName} ${cm.user.profile.lastName}`,
            value: `${cm.user.profile.firstName} ${cm.user.profile.lastName}`,
          };
        }
        return null;
      }).filter((c) => !!c);
      cb(mapped);
    } else {
      cb([]);
    }
  }, [companyMembersData]);

  const uri = `${config.REACT_APP_PLATFORM_URI}/share/report/${shareCode}`;

  const updatedReportData = useMemo(() => {
    if (error || loading) return null;
    return processVideoUrls(reportData, testSessionNotes, highlightReels);
  }, [error, loading, reportData, testSessionNotes, highlightReels]);

  if (error || companyMembersError) {
    return (
      <ErrorHandler
        error={error || companyMembersError}
        defaultMessage="Error while loading reports. Please check your internet connection."
      />
    );
  }

  if ((loading || companyMembersLoading) && !touched) {
    return (
      <Container>
        <Loader data-loader="report-edit-loader" />
      </Container>
    );
  }

  return (
    <Container>
      <ContainerHeader>
        <Input
          onChange={handleTitleChange}
          value={title}
        />
        <FeedbackMessage>
          {feedback}
        </FeedbackMessage>
        <Button onClick={() => onSetShareModalOpen(shareModalOpen)}>Share</Button>
      </ContainerHeader>
      <ShareDialog
        show={shareModalOpen}
        title={`Share ${title}`}
        handleClose={() => onSetShareModalOpen(shareModalOpen)}
        toggleLabel="Enable sharing"
        uri={uri}
        handleUpdate={handleUpdateShare}
        shareEnabled={shareEnabled}
        sharePassword={sharePassword}
      />
      <ContainerFroala>
        <Froala
          reportData={updatedReportData}
          onReportDataChange={handleReportDataChange}
          onClickAddResearch={handleModalOpen}
          modalOpen={modalOpen}
          handleModalOpen={handleModalOpen}
          companyId={project.company.id}
          project={project}
          docId={reportId}
          codoxUser={{
            id: userProfile.id,
            name: `${userProfile.first_name} ${userProfile.last_name}`,
          }}
          fetchMentions={(cb) => handleFetchMembers(cb)}
        />
      </ContainerFroala>
    </Container>
  );
};

ReportEdit.propTypes = {
  project: PropTypes.shape({
    id: PropTypes.string,
    company: PropTypes.shape({
      id: PropTypes.string,
    }),
  }).isRequired,
  match: PropTypes.shape({
    params: PropTypes.shape({
      reportId: PropTypes.string,
    }),
  }).isRequired,
  userProfile: PropTypes.shape({
    id: PropTypes.string.isRequired,
    first_name: PropTypes.string.isRequired,
    last_name: PropTypes.string.isRequired,
  }).isRequired,
};

const mapStateToProps = ({ authentication }) => ({
  userProfile: authentication.profile,
});

const enhance = compose(
  connect(mapStateToProps, null),
  withProjectSidebar({ selectedTab: Tabs.PROJECT }),
);

export default enhance(ReportEdit);
